import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOrders,
  createOrder,
  updateOrderStatus,
  deleteOrder,
  fetchOrderStats,
} from "../redux/slices/ordersSlice";
import { fetchParts, fetchSuppliers } from "../redux/slices/partsSlice";
import { ordersAPI } from "../api/api";
import {
  Modal,
  ConfirmDialog,
  LoadingSpinner,
  StatsCard,
  EmptyState,
} from "../components/Common";
import { useSearch } from "../context/SearchContext";
import { useLanguage } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { printTable } from "../utils/printTable";

// Item statuses where nothing more is coming for that line.
const CLOSED_ITEM_STATUSES = ["cancelled", "nla", "closed"];
// Orders that are finished and can't be edited any more.
const FINISHED_ORDER_STATUSES = ["delivered", "cancelled", "completed"];

const Orders = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { searchTerm } = useSearch();
  const { t } = useLanguage();

  // Redux state
  const {
    items: orders,
    stats,
    loading,
  } = useSelector((state) => state.orders);
  const { items: parts, suppliers } = useSelector((state) => state.parts);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // State
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [deliveryItems, setDeliveryItems] = useState([]);
  const [deliveryNotes, setDeliveryNotes] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSupplier, setFilterSupplier] = useState("");
  const [newOrderItem, setNewOrderItem] = useState({
    part_id: "",
    quantity: 1,
    unit_price: "",
    notes: "",
  });

  // Edit order number / notes state
  const [editingOrderNumber, setEditingOrderNumber] = useState("");
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [editingNotes, setEditingNotes] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // Per-item notes editing (View modal)
  const [editingItemNotes, setEditingItemNotes] = useState({});

  // Transfer item state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferItem, setTransferItem] = useState(null);
  const [transferTargetOrderId, setTransferTargetOrderId] = useState("");
  const [transferQty, setTransferQty] = useState("");

  // Form
  const [formData, setFormData] = useState({
    order_number: "",
    notes: "",
    items: [],
  });
  const [currentItem, setCurrentItem] = useState({
    part_id: "",
    quantity: 1,
    unit_price: "",
    notes: "",
  });

  useEffect(() => {
    dispatch(fetchOrders());
    dispatch(fetchOrderStats());
    dispatch(fetchParts());
    dispatch(fetchSuppliers());
  }, [dispatch]);

  // Filter orders — includes part/item name search via parts_name
  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.created_by_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.fullname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.parts_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = !filterStatus || order.status === filterStatus;

    const matchesSupplier =
      !filterSupplier ||
      parts
        .filter((p) => p.supplier === filterSupplier)
        .some((p) =>
          order.parts_name?.toLowerCase().includes(p.name.toLowerCase()),
        );

    return matchesSearch && matchesStatus && matchesSupplier;
  });

  // Handlers
  const resetForm = () => {
    setFormData({ order_number: "", notes: "", items: [] });
    setCurrentItem({ part_id: "", quantity: 1, unit_price: "", notes: "" });
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (formData.items.length === 0) {
      alert(t("orders.alertAddOneItem"));
      return;
    }

    try {
      await dispatch(createOrder(formData)).unwrap();
      setShowCreateModal(false);
      resetForm();
      dispatch(fetchOrderStats());
    } catch (err) {
      alert(t("orders.alertCreateFailed", { error: err.message || err }));
    }
  };

  const addItemToList = () => {
    if (!currentItem.part_id || currentItem.quantity <= 0) {
      alert(t("orders.alertSelectPartAndQty"));
      return;
    }

    const part = parts.find((p) => p.id === parseInt(currentItem.part_id));
    if (!part) {
      alert(t("orders.alertPartNotFound"));
      return;
    }

    const exists = formData.items.find(
      (item) => item.part_id === parseInt(currentItem.part_id),
    );
    if (exists) {
      alert(t("orders.alertPartAlreadyAdded"));
      return;
    }

    const unitPrice = currentItem.unit_price || part.purchase_price || 0;

    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          part_id: parseInt(currentItem.part_id),
          quantity: parseInt(currentItem.quantity),
          unit_price: parseFloat(unitPrice),
          notes: currentItem.notes || "",
          part_name: part.name,
          part_color: part.color,
          part_sku: part.sku,
        },
      ],
    });

    setCurrentItem({ part_id: "", quantity: 1, unit_price: "", notes: "" });
  };

  const removeItemFromList = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const updateItemField = (index, field, value) => {
    const updated = [...formData.items];
    updated[index][field] = value;
    setFormData({ ...formData, items: updated });
  };

  const handleViewOrder = async (order) => {
    try {
      const response = await ordersAPI.getById(order.id);
      const data = response.data.data;
      setOrderDetails(data);
      setEditingOrderNumber(data.order_number || "");
      setEditingNotes(data.notes || "");
      const notesMap = {};
      data.items?.forEach((i) => {
        notesMap[i.id] = i.item_notes || i.notes || "";
      });
      setEditingItemNotes(notesMap);
      setShowViewModal(true);
    } catch (err) {
      alert(t("orders.alertLoadFailed", { error: err.message || err }));
    }
  };

  // Save edited order number
  const handleSaveOrderNumber = async () => {
    if (!editingOrderNumber.trim()) {
      alert(t("orders.alertOrderNumberEmpty"));
      return;
    }
    try {
      setIsSavingOrder(true);
      await ordersAPI.update(orderDetails.id, {
        order_number: editingOrderNumber.trim(),
        notes: orderDetails.notes,
      });
      const response = await ordersAPI.getById(orderDetails.id);
      if (response.data.success) {
        setOrderDetails(response.data.data);
        setEditingOrderNumber(response.data.data.order_number || "");
      }
      dispatch(fetchOrders());
    } catch (err) {
      alert(t("orders.alertUpdateOrderNumberFailed", { error: err.message }));
    } finally {
      setIsSavingOrder(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setIsSavingNotes(true);
      await ordersAPI.update(orderDetails.id, {
        order_number: orderDetails.order_number,
        notes: editingNotes,
      });
      const response = await ordersAPI.getById(orderDetails.id);
      if (response.data.success) {
        setOrderDetails(response.data.data);
        setEditingNotes(response.data.data.notes || "");
      }
      dispatch(fetchOrders());
    } catch (err) {
      alert(t("orders.alertUpdateNotesFailed", { error: err.message }));
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleSaveItemNotes = async (itemId) => {
    try {
      await ordersAPI.updateItem(itemId, { notes: editingItemNotes[itemId] });
      const response = await ordersAPI.getById(orderDetails.id);
      if (response.data.success) {
        const data = response.data.data;
        setOrderDetails(data);
        const notesMap = {};
        data.items?.forEach((i) => {
          notesMap[i.id] = i.item_notes || i.notes || "";
        });
        setEditingItemNotes(notesMap);
      }
    } catch (err) {
      alert(t("orders.alertSaveNotesFailed", { error: err.message }));
    }
  };

  const handleSaveDeliveryItemNotes = async (itemId, notes) => {
    try {
      await ordersAPI.updateItem(itemId, { notes });
      const response = await ordersAPI.getById(selectedOrder.id);
      if (response.data.success) {
        setSelectedOrder(response.data.data);
      }
    } catch (err) {
      alert(t("orders.alertSaveNotesFailed", { error: err.message }));
    }
  };

  const handleUpdateItemStatus = async (itemId, newStatus) => {
    try {
      await ordersAPI.updateItem(itemId, { item_status: newStatus });
      const response = await ordersAPI.getById(orderDetails.id);
      if (response.data.success) {
        setOrderDetails(response.data.data);
      }
      dispatch(fetchOrders());
      dispatch(fetchOrderStats());
      if (newStatus === "nla") dispatch(fetchParts());
    } catch (err) {
      alert(t("orders.alertUpdateItemStatusFailed", { error: err.message }));
    }
  };

  const handleTransferItem = async () => {
    if (!transferTargetOrderId || !transferQty || parseInt(transferQty) <= 0) {
      alert(t("orders.alertSelectTargetOrder"));
      return;
    }
    const qty = parseInt(transferQty);
    if (qty > transferItem.quantity_ordered) {
      alert(t("orders.alertCannotTransferMore"));
      return;
    }
    try {
      await ordersAPI.addItem(parseInt(transferTargetOrderId), {
        part_id: transferItem.part_id,
        quantity: qty,
        unit_price: transferItem.unit_price || 0,
        notes: transferItem.notes || null,
      });

      if (qty >= transferItem.quantity_ordered) {
        await ordersAPI.deleteItem(transferItem.id);
      } else {
        await ordersAPI.updateItem(transferItem.id, {
          quantity: transferItem.quantity_ordered - qty,
        });
      }

      setShowTransferModal(false);
      setTransferItem(null);
      setTransferTargetOrderId("");
      setTransferQty("");

      if (orderDetails) {
        const response = await ordersAPI.getById(orderDetails.id);
        if (response.data.success) {
          const data = response.data.data;
          setOrderDetails(data);
          const notesMap = {};
          data.items?.forEach((i) => {
            notesMap[i.id] = i.item_notes || i.notes || "";
          });
          setEditingItemNotes(notesMap);
        }
      }

      if (selectedOrder) {
        const response = await ordersAPI.getById(selectedOrder.id);
        if (response.data.success) {
          const fullOrder = response.data.data;
          setDeliveryItems(
            fullOrder.items?.map((item) => {
              const ordered = item.quantity_ordered || 0;
              const alreadyDelivered = item.quantity_delivered || 0;
              return {
                id: item.id,
                part_name: item.part_name,
                part_color: item.part_color,
                quantity_ordered: ordered,
                quantity_already_delivered: alreadyDelivered,
                quantity_receiving: 0,
                quantity_delivered: alreadyDelivered,
                quantity_backorder:
                  item.quantity_backorder || ordered - alreadyDelivered,
                item_status: CLOSED_ITEM_STATUSES.includes(item.status)
                  ? item.status
                  : "partial",
                notes: item.notes || "",
              };
            }) || [],
          );
        }
      }

      dispatch(fetchOrders());
      dispatch(fetchOrderStats());
    } catch (err) {
      alert(t("orders.alertTransferFailed", { error: err.message }));
    }
  };

  const handleOpenDeliveryModal = async (order) => {
    try {
      const response = await ordersAPI.getById(order.id);
      const fullOrder = response.data.data;

      setSelectedOrder(fullOrder);

      setDeliveryItems(
        fullOrder.items?.map((item) => {
          const ordered = item.quantity_ordered || 0;
          const alreadyDelivered = item.quantity_delivered || 0;

          return {
            id: item.id,
            part_name: item.part_name,
            quantity_ordered: ordered,
            quantity_already_delivered: alreadyDelivered,
            quantity_receiving: 0,
            quantity_delivered: alreadyDelivered,
            quantity_backorder:
              item.quantity_backorder || ordered - alreadyDelivered,
            item_status: CLOSED_ITEM_STATUSES.includes(item.status)
              ? item.status
              : "partial",
            notes: item.item_notes || item.notes || "",
          };
        }) || [],
      );
      setShowDeliveryModal(true);
    } catch (err) {
      alert(t("orders.alertLoadFailed", { error: err.message }));
    }
  };

  const handleReceivingChange = (index, receivingQty) => {
    const updated = [...deliveryItems];
    const item = updated[index];

    const qtyIn = Math.max(0, parseInt(receivingQty) || 0);
    const alreadyDelivered = item.quantity_already_delivered || 0;
    const ordered = item.quantity_ordered;

    const totalDelivered = alreadyDelivered + qtyIn;
    const remaining = ordered - totalDelivered;

    item.quantity_receiving = qtyIn;
    item.quantity_delivered = totalDelivered;
    item.quantity_backorder = Math.max(0, remaining);

    if (!CLOSED_ITEM_STATUSES.includes(item.item_status)) {
      item.item_status = totalDelivered >= ordered ? "delivered" : "partial";
    }

    setDeliveryItems(updated);
  };

  const handleItemStatusChange = (index, newStatus) => {
    const updated = [...deliveryItems];
    const item = updated[index];

    item.item_status = newStatus;

    if (newStatus === "cancelled") {
      item.quantity_receiving = 0;
      item.quantity_backorder = 0;
    }

    setDeliveryItems(updated);
  };

  const handleDeliverySubmit = async (e) => {
    e.preventDefault();

    const stillHasMissingItems = deliveryItems.some((item) => {
      if (CLOSED_ITEM_STATUSES.includes(item.item_status)) return false;
      const currentTotal =
        (item.quantity_already_delivered || 0) +
        (parseInt(item.quantity_receiving) || 0);
      return currentTotal < item.quantity_ordered;
    });

    try {
      const itemsPayload = deliveryItems.map((item) => {
        const totalForThisItem =
          (item.quantity_already_delivered || 0) +
          (parseInt(item.quantity_receiving) || 0);

        return {
          id: item.id,
          quantity_delivered: parseInt(item.quantity_receiving) || 0,
          item_status: CLOSED_ITEM_STATUSES.includes(item.item_status)
            ? item.item_status
            : totalForThisItem >= item.quantity_ordered
                ? "delivered"
                : "partial",
          notes: item.notes,
        };
      });

      await dispatch(
        updateOrderStatus({
          id: selectedOrder.id,
          status: stillHasMissingItems ? "partial" : "delivered",
          notes: deliveryNotes,
          items: itemsPayload,
        }),
      ).unwrap();

      setShowDeliveryModal(false);
      dispatch(fetchOrders());
      dispatch(fetchOrderStats());
      if (deliveryItems.some((i) => i.item_status === "nla")) {
        dispatch(fetchParts());
      }
    } catch (err) {
      alert(t("orders.alertGenericFailed", { error: err.message }));
    }
  };

  const handleStatusChange = async (order, newStatus) => {
    try {
      await dispatch(
        updateOrderStatus({
          id: order.id,
          status: newStatus,
          notes: "",
          items: [],
        }),
      ).unwrap();
      dispatch(fetchOrders());
      dispatch(fetchOrderStats());
    } catch (err) {
      alert(t("orders.alertUpdateStatusFailed", { error: err.message || err }));
    }
  };

  // Close whatever hasn't arrived yet (supplier NLA, 28 of 30, ...) so the
  // order stops sitting in backorder.
  const handleCompleteOrder = async (order) => {
    if (
      !window.confirm(
        t("orders.confirmCompleteOrder", {
          order: order.order_number || `#${order.id}`,
        }),
      )
    ) {
      return;
    }
    try {
      await ordersAPI.complete(order.id, {});
      dispatch(fetchOrders());
      dispatch(fetchOrderStats());
      if (orderDetails?.id === order.id) {
        const response = await ordersAPI.getById(order.id);
        if (response.data.success) setOrderDetails(response.data.data);
      }
    } catch (err) {
      alert(
        t("orders.alertCompleteFailed", {
          error: err.response?.data?.error || err.message,
        }),
      );
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteOrder(selectedOrder.id)).unwrap();
      setShowDeleteDialog(false);
      setSelectedOrder(null);
      dispatch(fetchOrderStats());
    } catch (err) {
      alert(t("orders.alertCancelOrderFailed", { error: err.message || err }));
    }
  };

  // Helpers
  const getStatusColor = (status) => {
    const colors = {
      draft: "bg-gray-100 text-gray-800",
      pending: "bg-yellow-100 text-yellow-800",
      ordered: "bg-purple-100 text-purple-800",
      delivered: "bg-green-100 text-green-800",
      partial: "bg-orange-100 text-orange-800",
      cancelled: "bg-red-100 text-red-800",
      completed: "bg-teal-100 text-teal-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getItemStatusColor = (status) => {
    const colors = {
      pending: "bg-gray-100 text-gray-800",
      delivered: "bg-green-100 text-green-800",
      partial: "bg-orange-100 text-orange-800",
      backorder: "bg-yellow-100 text-yellow-800",
      cancelled: "bg-red-100 text-red-800",
      nla: "bg-gray-700 text-white",
      closed: "bg-slate-200 text-slate-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const STATUS_KEYS = [
    "draft",
    "pending",
    "ordered",
    "delivered",
    "partial",
    "cancelled",
    "backorder",
    "completed",
    "nla",
    "closed",
  ];

  const formatStatus = (status) => {
    if (!status) return "-";
    if (STATUS_KEYS.includes(status)) return t(`orders.status.${status}`);
    return status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const calculateTotal = (items) => {
    return items.reduce(
      (total, item) =>
        total + (parseFloat(item.unit_price) || 0) * item.quantity,
      0,
    );
  };

  // Add item to existing order
  const handleAddItemToOrder = async () => {
    if (!newOrderItem.part_id || !newOrderItem.quantity) {
      alert(t("orders.alertSelectPartAndEnterQty"));
      return;
    }

    try {
      await ordersAPI.addItem(orderDetails.id, {
        part_id: parseInt(newOrderItem.part_id),
        quantity: parseInt(newOrderItem.quantity),
        unit_price: parseFloat(newOrderItem.unit_price) || 0,
        notes: newOrderItem.notes || null,
      });

      setNewOrderItem({ part_id: "", quantity: 1, unit_price: "", notes: "" });

      const response = await ordersAPI.getById(orderDetails.id);
      if (response.data.success) {
        setOrderDetails(response.data.data);
      }

      dispatch(fetchOrders());
      dispatch(fetchOrderStats());
    } catch (error) {
      alert(t("orders.alertAddItemFailed", { error: error.message }));
    }
  };

  // Delete item from order
  const handleDeleteOrderItem = async (itemId) => {
    if (!window.confirm(t("orders.confirmRemoveItem"))) {
      return;
    }

    try {
      await ordersAPI.deleteItem(itemId);

      const response = await ordersAPI.getById(orderDetails.id);
      if (response.data.success) {
        setOrderDetails(response.data.data);
      }

      dispatch(fetchOrders());
      dispatch(fetchOrderStats());
    } catch (error) {
      alert(t("orders.alertDeleteItemFailed", { error: error.message }));
    }
  };

  // Shared react-select styles
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "0.375rem",
      borderColor: state.isFocused ? "#3B82F6" : "#D1D5DB",
      boxShadow: state.isFocused ? "0 0 0 1px #3B82F6" : "none",
      minHeight: "42px",
      "&:hover": { borderColor: "#3B82F6" },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#3B82F6"
        : state.isFocused
          ? "#EFF6FF"
          : "white",
      color: state.isSelected ? "white" : "#1F2937",
      cursor: "pointer",
      padding: "8px 12px",
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    placeholder: (base) => ({ ...base, color: "#9CA3AF" }),
  };

  const formatOptionLabel = (option) => {
    const part = option.originalPart;
    if (!part) return option.label;
    return (
      <div className="flex justify-between items-center">
        <div>
          <span className="font-medium">{part.name}</span>
          {part.color && (
            <span className="text-gray-500 ml-1">({part.color})</span>
          )}
          {part.sku && (
            <span className="text-gray-400 text-xs ml-2">{part.sku}</span>
          )}
          {!!part.is_nla && (
            <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-gray-700 text-white">
              {t("orders.nlaBadge")}
            </span>
          )}
        </div>
        <div className="text-right text-sm">
          <span className="text-green-600 font-medium">
            CHF {part.purchase_price || 0}
          </span>
          <span className="text-gray-400 ml-2">
            {t("orders.stockLabel")} {part.quantity || 0}
          </span>
        </div>
      </div>
    );
  };

  const partOptions = parts.map((p) => ({
    value: p.id,
    label: `${p.name}${p.color ? ` (${p.color})` : ""} - ${p.sku || t("orders.noSku")}`,
    originalPart: p,
  }));

  const getPartSelectValue = (partId) => {
    if (!partId) return null;
    const p = parts.find((part) => part.id === parseInt(partId));
    if (!p) return null;
    return {
      value: p.id,
      label: `${p.name}${p.color ? ` (${p.color})` : ""} - ${p.sku || t("orders.noSku")}`,
      originalPart: p,
    };
  };

  const handlePrint = () => {
    printTable({
      title: t("orders.title"),
      subtitle: [filterStatus && formatStatus(filterStatus), filterSupplier]
        .filter(Boolean)
        .join(", "),
      columns: [
        { header: t("orders.colOrder") },
        { header: t("orders.colItems") },
        { header: t("common.total"), align: "right" },
        { header: t("common.notes") },
        { header: t("common.status") },
        { header: t("common.date") },
      ],
      rows: filteredOrders.map((order) => [
        `${order.order_number || `#${order.id}`}${order.fullname ? ` – ${order.fullname}` : ""}`,
        order.parts_name,
        `CHF ${parseFloat(order.total_amount || 0).toFixed(2)}`,
        order.notes,
        formatStatus(order.status),
        formatDate(order.created_at),
      ]),
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t("orders.title")}</h1>
          <p className="text-gray-600 mt-1">{t("orders.subtitle")}</p>
        </div>
        <div className="flex flex-row gap-4">
          <button onClick={handlePrint} className="btn-secondary">
            🖨️ {t("common.print")}
          </button>
          <button
            onClick={() => navigate("/orders/parts-report")}
            className="btn-secondary"
          >
            {t("orders.partsReport")}
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            {t("orders.createOrder")}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <StatsCard
          label={t("orders.statTotal")}
          value={stats?.total_orders || orders.length}
          color="gray"
        />
        <StatsCard
          label={t("orders.statPending")}
          value={Number(stats?.pending) || 0}
          color="orange"
        />
        <StatsCard
          label={t("orders.statOrdered")}
          value={Number(stats?.ordered) || 0}
          color="purple"
        />
        <StatsCard
          label={t("orders.statDelivered")}
          value={Number(stats?.delivered) || 0}
          color="green"
        />
        <StatsCard
          label={t("orders.statPartial")}
          value={Number(stats?.partial) || 0}
          color="red"
        />
        <StatsCard
          label={t("orders.statCompleted")}
          value={Number(stats?.completed) || 0}
          color="blue"
        />
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            {searchTerm && (
              <p className="text-sm text-gray-600">
                {t("orders.searchingLabel")} <span className="font-medium">"{searchTerm}"</span>
                <span className="text-gray-400 ml-2 text-xs">
                  {t("orders.searchingHint")}
                </span>
              </p>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
              className="input-field"
            >
              <option value="">{t("orders.allSuppliers")}</option>
              {suppliers.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input-field"
            >
              <option value="">{t("orders.allStatus")}</option>
              <option value="draft">{t("orders.status.draft")}</option>
              <option value="pending">{t("orders.status.pending")}</option>
              <option value="ordered">{t("orders.status.ordered")}</option>
              <option value="delivered">{t("orders.status.delivered")}</option>
              <option value="partial">{t("orders.status.partial")}</option>
              <option value="completed">{t("orders.status.completed")}</option>
              <option value="cancelled">{t("orders.status.cancelled")}</option>
            </select>
            {(filterStatus || filterSupplier) && (
              <button
                onClick={() => {
                  setFilterStatus("");
                  setFilterSupplier("");
                }}
                className="btn-secondary"
              >
                {t("orders.clearButton")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <LoadingSpinner />
      ) : filteredOrders.length === 0 ? (
        <EmptyState
          icon="📦"
          title={t("orders.emptyTitle")}
          message={t("orders.emptyMessage")}
          actionLabel={t("orders.createOrder")}
          onAction={() => setShowCreateModal(true)}
          hasItems={orders.length > 0}
        />
      ) : (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("orders.colOrder")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("orders.colItems")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("common.total")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("common.notes")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("common.status")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t("common.date")}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  {t("common.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900">
                      {order.order_number || `#${order.id}`}
                    </p>
                    <p className="text-xs text-gray-500">
                      {order.fullname || "-"}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs text-gray-600">
                      {order.parts_name || 0}
                    </p>
                    {order.total_backorder > 0 && (
                      <p className="text-xs text-orange-600">
                        {t("orders.backorderCount", { count: order.total_backorder })}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold">
                      CHF {parseFloat(order.total_amount || 0).toFixed(2)}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-600 max-w-xs truncate">
                      {order.notes || "-"}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}
                    >
                      {formatStatus(order.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formatDate(order.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm space-x-1">
                    <button
                      onClick={() => handleViewOrder(order)}
                      className="text-purple-600 hover:text-purple-900"
                    >
                      {t("orders.view")}
                    </button>

                    {order.status === "draft" && (
                      <button
                        onClick={() => handleStatusChange(order, "ordered")}
                        className="text-purple-600 hover:text-purple-900"
                      >
                        {t("orders.moveToOrdered")}
                      </button>
                    )}

                    {order.status === "ordered" && (
                      <button
                        onClick={() => handleOpenDeliveryModal(order)}
                        className="text-green-600 hover:text-green-900 font-medium"
                      >
                        {t("orders.receive")}
                      </button>
                    )}

                    {order.status === "partial" && (
                      <button
                        onClick={() => handleOpenDeliveryModal(order)}
                        className="text-orange-600 hover:text-orange-900 font-medium"
                      >
                        {t("orders.receiveMore")}
                      </button>
                    )}

                    {["ordered", "partial"].includes(order.status) && (
                      <button
                        onClick={() => handleCompleteOrder(order)}
                        className="text-teal-600 hover:text-teal-900"
                        title={t("orders.completeOrderTitle")}
                      >
                        {t("orders.completeOrder")}
                      </button>
                    )}

                    {!FINISHED_ORDER_STATUSES.includes(order.status) && (
                      <button
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowDeleteDialog(true);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        {t("common.cancel")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create Modal ── */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title={t("orders.createOrderPlain")}
        size="xl"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("orders.orderNumberLabel")}
              </label>
              <input
                type="text"
                value={formData.order_number}
                onChange={(e) =>
                  setFormData({ ...formData, order_number: e.target.value })
                }
                className="input-field"
                placeholder={t("orders.orderNumberPlaceholder")}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("orders.orderNotesLabel")}
              </label>
              <input
                type="text"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                className="input-field"
                placeholder={t("orders.orderNotesPlaceholderGeneral")}
              />
            </div>
          </div>

          {/* Add Items */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("orders.addItemsLabel", { count: formData.items.length })}
            </label>
            <div className="bg-gray-50 p-3 rounded-lg mb-3 space-y-2">
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-5">
                  <Select
                    classNamePrefix="react-select"
                    options={partOptions}
                    value={getPartSelectValue(currentItem.part_id)}
                    onChange={(selectedOption) => {
                      const part = selectedOption?.originalPart || null;
                      setCurrentItem({
                        ...currentItem,
                        part_id: selectedOption
                          ? selectedOption.value.toString()
                          : "",
                        unit_price: part?.purchase_price || "",
                      });
                    }}
                    placeholder={t("orders.searchPartPlaceholder")}
                    isClearable
                    isSearchable
                    noOptionsMessage={() => t("orders.noPartsFound")}
                    styles={selectStyles}
                    formatOptionLabel={formatOptionLabel}
                  />
                </div>
                <input
                  type="number"
                  value={currentItem.quantity}
                  onChange={(e) =>
                    setCurrentItem({ ...currentItem, quantity: e.target.value })
                  }
                  className="input-field col-span-2"
                  placeholder={t("orders.qty")}
                  min="1"
                />
                <input
                  type="number"
                  step="0.01"
                  value={currentItem.unit_price}
                  onChange={(e) =>
                    setCurrentItem({
                      ...currentItem,
                      unit_price: e.target.value,
                    })
                  }
                  className="input-field col-span-2"
                  placeholder={t("common.price")}
                />
                <button
                  type="button"
                  onClick={addItemToList}
                  className="btn-success col-span-3"
                  disabled={!currentItem.part_id || !currentItem.quantity}
                >
                  {t("orders.addItemButton")}
                </button>
              </div>
              <input
                type="text"
                value={currentItem.notes}
                onChange={(e) =>
                  setCurrentItem({ ...currentItem, notes: e.target.value })
                }
                className="input-field"
                placeholder={t("orders.itemNotesPlaceholder")}
              />
            </div>

            {formData.items.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                        {t("orders.part")}
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">
                        {t("orders.qty")}
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">
                        {t("common.price")}
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                        {t("common.notes")}
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">
                        {t("orders.subtotal")}
                      </th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {formData.items.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm">
                          <p className="font-medium">{item.part_name}</p>
                          {item.part_color && (
                            <p className="text-xs text-gray-500">
                              {item.part_color}
                            </p>
                          )}
                          {item.part_sku && (
                            <p className="text-xs text-gray-400">
                              {item.part_sku}
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) =>
                              updateItemField(
                                index,
                                "quantity",
                                parseInt(e.target.value) || 1,
                              )
                            }
                            className="w-16 input-field text-center text-sm"
                          />
                        </td>
                        <td className="px-3 py-2 text-center text-sm">
                          CHF {parseFloat(item.unit_price).toFixed(2)}
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={item.notes || ""}
                            onChange={(e) =>
                              updateItemField(index, "notes", e.target.value)
                            }
                            className="w-full input-field text-sm"
                            placeholder={t("orders.itemNotesPlaceholderShort")}
                          />
                        </td>
                        <td className="px-3 py-2 text-center text-sm font-medium">
                          CHF {(item.unit_price * item.quantity).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => removeItemFromList(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td colSpan="4" className="px-3 py-2 text-right">
                        {t("orders.totalColon")}
                      </td>
                      <td className="px-3 py-2 text-center">
                        CHF {calculateTotal(formData.items).toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
              className="btn-secondary"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={formData.items.length === 0}
            >
              {t("orders.createOrderPlain")}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Delivery Modal ── */}
      <Modal
        isOpen={showDeliveryModal}
        onClose={() => {
          setShowDeliveryModal(false);
          setSelectedOrder(null);
        }}
        title={t("orders.receiveDeliveryTitle", {
          order: selectedOrder?.order_number || "#" + selectedOrder?.id,
        })}
        size="xl"
      >
        <form onSubmit={handleDeliverySubmit} className="space-y-3">
          <div className="border rounded-lg overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                    {t("orders.part")}
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">
                    {t("orders.ordered")}
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">
                    {t("orders.receiving")}
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">
                    {t("common.status")}
                  </th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {deliveryItems.map((item, index) => {
                  const isFullyDelivered =
                    (item.quantity_already_delivered || 0) >=
                    item.quantity_ordered;
                  return (
                    <tr
                      key={item.id}
                      className={isFullyDelivered ? "bg-green-50" : ""}
                    >
                      <td className="px-3 py-2">
                        <p className="text-sm font-medium">{item.part_name}</p>
                        {item.part_color && (
                          <p className="text-xs text-gray-400">
                            {item.part_color}
                          </p>
                        )}
                        <div className="flex items-center gap-1 mt-1">
                          <input
                            type="text"
                            value={item.notes}
                            onChange={(e) => {
                              const updated = [...deliveryItems];
                              updated[index].notes = e.target.value;
                              setDeliveryItems(updated);
                            }}
                            className="text-xs border border-gray-200 rounded px-1.5 py-0.5 w-48 placeholder-gray-300"
                            placeholder={t("orders.notesPlaceholderShort")}
                          />
                          {item.notes !==
                            (selectedOrder?.items?.find((i) => i.id === item.id)
                              ?.item_notes ||
                              selectedOrder?.items?.find(
                                (i) => i.id === item.id,
                              )?.notes ||
                              "") && (
                            <button
                              type="button"
                              onClick={() =>
                                handleSaveDeliveryItemNotes(item.id, item.notes)
                              }
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              {t("common.save")}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center text-sm">
                        <span className="font-medium">
                          {item.quantity_ordered}
                        </span>
                        {(item.quantity_already_delivered || 0) > 0 && (
                          <p className="text-xs text-green-600">
                            {t("orders.doneCount", { count: item.quantity_already_delivered })}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {isFullyDelivered ? (
                          <span className="text-green-600 text-xs">{t("orders.doneCheck")}</span>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            value={item.quantity_receiving || 0}
                            onChange={(e) =>
                              handleReceivingChange(index, e.target.value)
                            }
                            className="w-16 input-field text-center text-sm"
                            disabled={item.item_status === "cancelled"}
                          />
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <select
                          value={item.item_status}
                          onChange={(e) =>
                            handleItemStatusChange(index, e.target.value)
                          }
                          className={`text-xs px-2 py-1 rounded-full border-0 cursor-pointer ${getItemStatusColor(item.item_status)}`}
                          disabled={isFullyDelivered}
                        >
                          <option value="delivered">{t("orders.status.delivered")}</option>
                          <option value="partial">{t("orders.status.partial")}</option>
                          <option value="backorder">{t("orders.status.backorder")}</option>
                          <option value="closed">{t("orders.status.closed")}</option>
                          <option value="nla">{t("orders.status.nla")}</option>
                          <option value="cancelled">{t("orders.status.cancelled")}</option>
                        </select>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {!isFullyDelivered && (
                          <button
                            type="button"
                            onClick={() => {
                              setTransferItem(item);
                              setTransferQty(String(item.quantity_ordered));
                              setTransferTargetOrderId("");
                              setShowTransferModal(true);
                            }}
                            className="text-blue-400 hover:text-blue-600 text-xs"
                            title={t("orders.moveToAnotherOrder")}
                          >
                            ↗
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <textarea
            value={deliveryNotes}
            onChange={(e) => setDeliveryNotes(e.target.value)}
            className="input-field w-full text-sm"
            rows="2"
            placeholder={t("orders.deliveryNotesPlaceholder")}
          />

          <div className="flex justify-end gap-3 pt-2 border-t">
            <button
              type="button"
              onClick={() => setShowDeliveryModal(false)}
              className="btn-secondary"
            >
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary">
              {t("common.confirm")}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── View / Edit Order Modal ── */}
      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setNewOrderItem({
            part_id: "",
            quantity: 1,
            unit_price: "",
            notes: "",
          });
        }}
        title={t("orders.orderTitle", {
          order: orderDetails?.order_number || "#" + orderDetails?.id,
        })}
        size="xl"
      >
        {orderDetails && (
          <div className="space-y-3">
            {/* Compact header row */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <input
                  type="text"
                  value={editingOrderNumber}
                  onChange={(e) => setEditingOrderNumber(e.target.value)}
                  className="input-field text-sm font-medium w-40"
                  placeholder={t("orders.orderNumberPlaceholderShort")}
                  disabled={FINISHED_ORDER_STATUSES.includes(
                    orderDetails.status,
                  )}
                />
                {editingOrderNumber !== orderDetails.order_number &&
                  !FINISHED_ORDER_STATUSES.includes(orderDetails.status) && (
                    <button
                      onClick={handleSaveOrderNumber}
                      disabled={isSavingOrder}
                      className="btn-primary text-xs px-2 py-1.5 whitespace-nowrap"
                    >
                      {isSavingOrder ? "..." : t("common.save")}
                    </button>
                  )}
              </div>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(orderDetails.status)}`}
              >
                {formatStatus(orderDetails.status)}
              </span>
              <span className="text-sm font-bold text-gray-800">
                CHF {parseFloat(orderDetails.total_amount || 0).toFixed(2)}
              </span>
              <span className="text-xs text-gray-400">
                {formatDate(orderDetails.created_at)}
              </span>
            </div>

            {/* Items */}
            <div className="border rounded-lg overflow-hidden">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                      {t("orders.part")}
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">
                      {t("orders.ordered")}
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">
                      {t("orders.delivered")}
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">
                      {t("common.status")}
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {orderDetails.items?.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <p className="text-sm font-medium">{item.part_name}</p>
                        {item.part_color && (
                          <p className="text-xs text-gray-400">
                            {item.part_color}
                          </p>
                        )}
                        <div className="flex items-center gap-1 mt-1">
                          <input
                            type="text"
                            value={
                              editingItemNotes[item.id] ??
                              (item.item_notes || item.notes || "")
                            }
                            onChange={(e) =>
                              setEditingItemNotes((prev) => ({
                                ...prev,
                                [item.id]: e.target.value,
                              }))
                            }
                            className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white placeholder-gray-300 w-48"
                            placeholder={t("orders.notesPlaceholderShort")}
                          />
                          {editingItemNotes[item.id] !== undefined &&
                            editingItemNotes[item.id] !==
                              (item.item_notes || item.notes || "") && (
                              <button
                                onClick={() => handleSaveItemNotes(item.id)}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                              >
                                {t("common.save")}
                              </button>
                            )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center text-sm font-medium">
                        {item.quantity_ordered}
                      </td>
                      <td className="px-3 py-2 text-center text-sm text-green-600">
                        {item.quantity_delivered || 0}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <select
                          value={item.status || "pending"}
                          onChange={(e) =>
                            handleUpdateItemStatus(item.id, e.target.value)
                          }
                          className={`text-xs px-2 py-1 rounded-full border-0 cursor-pointer font-medium ${getItemStatusColor(item.status)}`}
                        >
                          <option value="pending">{t("orders.status.pending")}</option>
                          <option value="partial">{t("orders.status.partial")}</option>
                          <option value="delivered">{t("orders.status.delivered")}</option>
                          <option value="backorder">{t("orders.status.backorder")}</option>
                          <option value="closed">{t("orders.status.closed")}</option>
                          <option value="nla">{t("orders.status.nla")}</option>
                          <option value="cancelled">{t("orders.status.cancelled")}</option>
                        </select>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {(item.quantity_delivered || 0) === 0 && (
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => {
                                setTransferItem(item);
                                setTransferQty(String(item.quantity_ordered));
                                setTransferTargetOrderId("");
                                setShowTransferModal(true);
                              }}
                              className="text-blue-400 hover:text-blue-600 text-xs"
                              title={t("orders.moveToAnotherOrder")}
                            >
                              ↗
                            </button>
                            <button
                              onClick={() => handleDeleteOrderItem(item.id)}
                              className="text-red-400 hover:text-red-600 text-xs"
                              title={t("common.remove")}
                            >
                              ✕
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Order notes */}
            <div className="flex items-center gap-2">
              <textarea
                value={editingNotes}
                onChange={(e) => setEditingNotes(e.target.value)}
                className="input-field flex-1 text-sm"
                rows="2"
                placeholder={t("orders.orderNotesPlaceholder")}
              />
              {editingNotes !== (orderDetails.notes || "") && (
                <button
                  onClick={handleSaveNotes}
                  disabled={isSavingNotes}
                  className="btn-primary text-xs px-2 py-1.5 whitespace-nowrap"
                >
                  {isSavingNotes ? "..." : t("common.save")}
                </button>
              )}
            </div>

            {/* Add item — only if not delivered/completed/cancelled */}
            {!FINISHED_ORDER_STATUSES.includes(orderDetails.status) && (
              <div className="border-t pt-3">
                <div className="flex gap-2 items-center flex-wrap">
                  <div className="flex-1 min-w-48">
                    <Select
                      classNamePrefix="react-select"
                      options={partOptions}
                      value={getPartSelectValue(newOrderItem.part_id)}
                      onChange={(selectedOption) => {
                        const part = selectedOption?.originalPart || null;
                        setNewOrderItem({
                          ...newOrderItem,
                          part_id: selectedOption
                            ? selectedOption.value.toString()
                            : "",
                          unit_price: part?.purchase_price || "",
                        });
                      }}
                      placeholder={t("orders.addPartPlaceholder")}
                      isClearable
                      isSearchable
                      noOptionsMessage={() => t("orders.noPartsFound")}
                      styles={selectStyles}
                      formatOptionLabel={formatOptionLabel}
                    />
                  </div>
                  <input
                    type="number"
                    value={newOrderItem.quantity}
                    onChange={(e) =>
                      setNewOrderItem({
                        ...newOrderItem,
                        quantity: e.target.value,
                      })
                    }
                    className="input-field w-16 text-sm"
                    placeholder={t("orders.qty")}
                    min="1"
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={newOrderItem.unit_price}
                    onChange={(e) =>
                      setNewOrderItem({
                        ...newOrderItem,
                        unit_price: e.target.value,
                      })
                    }
                    className="input-field w-20 text-sm"
                    placeholder="CHF"
                  />
                  <input
                    type="text"
                    value={newOrderItem.notes}
                    onChange={(e) =>
                      setNewOrderItem({
                        ...newOrderItem,
                        notes: e.target.value,
                      })
                    }
                    className="input-field w-32 text-sm"
                    placeholder={t("common.notes")}
                  />
                  <button
                    type="button"
                    onClick={handleAddItemToOrder}
                    className="btn-success text-sm whitespace-nowrap"
                    disabled={!newOrderItem.part_id || !newOrderItem.quantity}
                  >
                    {t("orders.addShort")}
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-3 border-t">
              {["ordered", "partial"].includes(orderDetails.status) && (
                <button
                  onClick={() => handleCompleteOrder(orderDetails)}
                  className="btn-secondary text-teal-700"
                  title={t("orders.completeOrderTitle")}
                >
                  {t("orders.completeOrder")}
                </button>
              )}
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setNewOrderItem({
                    part_id: "",
                    quantity: 1,
                    unit_price: "",
                    notes: "",
                  });
                }}
                className="btn-secondary"
              >
                {t("common.close")}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Transfer Item Modal ── */}
      <Modal
        isOpen={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
          setTransferItem(null);
          setTransferTargetOrderId("");
          setTransferQty("");
        }}
        title={t("orders.moveItemTitle")}
        size="sm"
      >
        {transferItem && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg text-sm">
              <p className="font-medium">{transferItem.part_name}</p>
              {transferItem.part_color && (
                <p className="text-xs text-gray-500">
                  {transferItem.part_color}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {t("orders.orderedQty", { qty: transferItem.quantity_ordered })}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("orders.quantityToMove")}
              </label>
              <input
                type="number"
                value={transferQty}
                onChange={(e) => setTransferQty(e.target.value)}
                className="input-field"
                min="1"
                max={transferItem.quantity_ordered}
                placeholder={t("orders.maxPlaceholder", { max: transferItem.quantity_ordered })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("orders.moveToOrderLabel")}
              </label>
              <select
                value={transferTargetOrderId}
                onChange={(e) => setTransferTargetOrderId(e.target.value)}
                className="input-field"
              >
                <option value="">{t("orders.selectOrderOption")}</option>
                {orders
                  .filter(
                    (o) =>
                      o.id !== orderDetails?.id &&
                      !FINISHED_ORDER_STATUSES.includes(o.status),
                  )
                  .map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.order_number || `#${o.id}`}
                      {o.notes ? ` — ${o.notes}` : ""}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t">
              <button
                onClick={() => {
                  setShowTransferModal(false);
                  setTransferItem(null);
                }}
                className="btn-secondary"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={handleTransferItem}
                disabled={!transferTargetOrderId || !transferQty}
                className="btn-primary"
              >
                {t("orders.moveItemButton")}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Delete Confirmation ── */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t("orders.cancelOrderTitle")}
        message={t("orders.cancelOrderMessage", {
          order: selectedOrder?.order_number || "#" + selectedOrder?.id,
        })}
      />
    </div>
  );
};

export default Orders;
